import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  footerNavMode: 'applications',
  theme: 'light',
  loading: {
    global: false,
    page: false,
    component: false,
  },
  notifications: [],
  modals: {
    isOpen: false,
    type: null,
    data: null,
  },
  toasts: [],
  breadcrumbs: [],
  pageTitle: 'Service ERP',
  searchQuery: '',
  filters: {},
  sortBy: null,
  sortOrder: 'asc',
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed
      localStorage.setItem('sidebarCollapsed', state.sidebarCollapsed)
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload
      localStorage.setItem('sidebarCollapsed', action.payload)
    },
    toggleSidebarMobile: (state) => {
      state.sidebarMobileOpen = !state.sidebarMobileOpen
    },
    setSidebarMobileOpen: (state, action) => {
      state.sidebarMobileOpen = Boolean(action.payload)
    },
    setFooterNavMode: (state, action) => {
      state.footerNavMode = action.payload
    },
    setTheme: (state, action) => {
      state.theme = action.payload
      localStorage.setItem('theme', action.payload)
      document.documentElement.setAttribute('data-theme', action.payload)
    },
    setLoading: (state, action) => {
      const { type, isLoading } = action.payload
      state.loading[type] = isLoading
    },
    addNotification: (state, action) => {
      const notification = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      }
      state.notifications.unshift(notification)
      
      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50)
      }
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      )
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    openModal: (state, action) => {
      state.modals = {
        isOpen: true,
        type: action.payload.type,
        data: action.payload.data || null,
      }
    },
    closeModal: (state) => {
      state.modals = {
        isOpen: false,
        type: null,
        data: null,
      }
    },
    addToast: (state, action) => {
      const toast = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      }
      state.toasts.push(toast)
    },
    removeToast: (state, action) => {
      state.toasts = state.toasts.filter(toast => toast.id !== action.payload)
    },
    clearToasts: (state) => {
      state.toasts = []
    },
    setBreadcrumbs: (state, action) => {
      state.breadcrumbs = action.payload
    },
    setPageTitle: (state, action) => {
      state.pageTitle = action.payload
      document.title = `${action.payload} - Service ERP`
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setSorting: (state, action) => {
      const { sortBy, sortOrder } = action.payload
      state.sortBy = sortBy
      state.sortOrder = sortOrder
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    resetPagination: (state) => {
      state.pagination = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      }
    },
    resetUI: (state) => {
      return { ...initialState, theme: state.theme }
    },
  },
})

export const {
  toggleSidebar,
  setSidebarCollapsed,
  toggleSidebarMobile,
  setSidebarMobileOpen,
  setFooterNavMode,
  setTheme,
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  addToast,
  removeToast,
  clearToasts,
  setBreadcrumbs,
  setPageTitle,
  setSearchQuery,
  setFilters,
  clearFilters,
  setSorting,
  setPagination,
  resetPagination,
  resetUI,
} = uiSlice.actions

export default uiSlice.reducer

